import React, { useState, useEffect, useRef, useCallback } from 'react';
import axios from 'axios';
import config from '../config';
import HelpTip from './HelpTip';
import { useTranslation } from 'react-i18next';

function SearchBar({ onPathFound, graphRef, prefillEntity }) {
  const { t } = useTranslation();
  const [waypoints, setWaypoints] = useState([
    { id: 0, query: '', results: [], selected: null, showResults: false, searching: false, alternatives: [], orQuery: '', orResults: [], orSearching: false, showOrSearch: false },
    { id: 1, query: '', results: [], selected: null, showResults: false, searching: false, alternatives: [], orQuery: '', orResults: [], orSearching: false, showOrSearch: false }
  ]);
  const [loading, setLoading] = useState(false);
  const [maxPathLength, setMaxPathLength] = useState(5);
  const [topK, setTopK] = useState(3);
  const [excludeInsolvent, setExcludeInsolvent] = useState(false);
  const [excludeForeign, setExcludeForeign] = useState(false);
  const [excludeInactive, setExcludeInactive] = useState(false);
  const [useMultiPoint, setUseMultiPoint] = useState(false);
  const [explorationMode, setExplorationMode] = useState(false);
  const [explorationStarted, setExplorationStarted] = useState(false);
  const [targetQuery, setTargetQuery] = useState('');
  const [targetResults, setTargetResults] = useState([]);
  const [selectedTarget, setSelectedTarget] = useState(null);
  const [showTargetResults, setShowTargetResults] = useState(false);
  const [pathLoading, setPathLoading] = useState(false);
  const [targetSearching, setTargetSearching] = useState(false);
  const debounceTimers = useRef({});

  // Handle prefill from entity modal "Find Paths From Here"
  useEffect(() => {
    if (prefillEntity) {
      updateWaypoint(0, {
        selected: prefillEntity,
        query: `${prefillEntity.name} (${prefillEntity.id})`,
        showResults: false
      });
      setExplorationMode(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [prefillEntity]);

  const updateWaypoint = useCallback((waypointId, updates) => {
    setWaypoints(prev => prev.map(wp =>
      wp.id === waypointId ? { ...wp, ...updates } : wp
    ));
  }, []);

  const searchEntities = useCallback(async (query, waypointId) => {
    if (query.length < 2) {
      updateWaypoint(waypointId, { results: [], searching: false });
      return;
    }

    updateWaypoint(waypointId, { searching: true });
    try {
      const response = await axios.get(`${config.API_BASE_URL}/api/search`, {
        params: { q: query }
      });
      updateWaypoint(waypointId, { results: response.data.results || [], searching: false });
    } catch (error) {
      console.error('Search error:', error);
      updateWaypoint(waypointId, { results: [], searching: false });
    }
  }, [updateWaypoint]);

  const handleWaypointChange = (waypointId, value) => {
    updateWaypoint(waypointId, { query: value, showResults: true, selected: null });

    // Debounce: wait 250ms after last keystroke before searching
    if (debounceTimers.current[waypointId]) {
      clearTimeout(debounceTimers.current[waypointId]);
    }
    if (value.length >= 2) {
      updateWaypoint(waypointId, { query: value, showResults: true, selected: null, searching: true });
      debounceTimers.current[waypointId] = setTimeout(() => {
        searchEntities(value, waypointId);
      }, 250);
    } else {
      updateWaypoint(waypointId, { query: value, showResults: false, results: [], searching: false });
    }
  };

  const selectWaypoint = (waypointId, entity) => {
    updateWaypoint(waypointId, {
      selected: entity,
      query: `${entity.name} (${entity.id})`,
      showResults: false
    });
  };

  const addWaypoint = () => {
    if (waypoints.length < 5) {
      setWaypoints([...waypoints, {
        id: Date.now(),
        query: '',
        results: [],
        selected: null,
        showResults: false,
        searching: false,
        alternatives: [],
        orQuery: '',
        orResults: [],
        orSearching: false,
        showOrSearch: false
      }]);
    }
  };

  const removeWaypoint = (waypointId) => {
    if (waypoints.length > 2) {
      setWaypoints(waypoints.filter(wp => wp.id !== waypointId));
    }
  };

  // --- OR alternative handlers ---
  const toggleOrSearch = (waypointId) => {
    const wp = waypoints.find(w => w.id === waypointId);
    const prefill = wp?.selected?.name || '';
    updateWaypoint(waypointId, {
      showOrSearch: true,
      orQuery: prefill,
      orResults: [],
      orSearching: prefill.length >= 2,
    });
    // Auto-search immediately with pre-filled name
    if (prefill.length >= 2) {
      const timerKey = `or_${waypointId}`;
      if (debounceTimers.current[timerKey]) clearTimeout(debounceTimers.current[timerKey]);
      debounceTimers.current[timerKey] = setTimeout(async () => {
        try {
          const response = await axios.get(`${config.API_BASE_URL}/api/search`, { params: { q: prefill } });
          updateWaypoint(waypointId, { orResults: response.data.results || [], orSearching: false });
        } catch {
          updateWaypoint(waypointId, { orResults: [], orSearching: false });
        }
      }, 0);
    }
  };

  const handleOrSearchChange = (waypointId, value) => {
    updateWaypoint(waypointId, { orQuery: value });
    const timerKey = `or_${waypointId}`;
    if (debounceTimers.current[timerKey]) {
      clearTimeout(debounceTimers.current[timerKey]);
    }
    if (value.length >= 2) {
      updateWaypoint(waypointId, { orQuery: value, orSearching: true });
      debounceTimers.current[timerKey] = setTimeout(async () => {
        try {
          const response = await axios.get(`${config.API_BASE_URL}/api/search`, {
            params: { q: value }
          });
          updateWaypoint(waypointId, { orResults: response.data.results || [], orSearching: false });
        } catch {
          updateWaypoint(waypointId, { orResults: [], orSearching: false });
        }
      }, 250);
    } else {
      updateWaypoint(waypointId, { orResults: [], orSearching: false });
    }
  };

  const selectOrEntity = (waypointId, entity) => {
    setWaypoints(prev => prev.map(wp => {
      if (wp.id !== waypointId) return wp;
      if (wp.alternatives.length >= 4) return wp; // max 4 alternatives
      if (wp.alternatives.some(a => a.id === entity.id)) return wp; // no duplicates
      if (wp.selected && wp.selected.id === entity.id) return wp; // not same as primary
      return {
        ...wp,
        alternatives: [...wp.alternatives, entity],
        orQuery: '',
        orResults: [],
        showOrSearch: false,
        orSearching: false
      };
    }));
  };

  const removeOrAlternative = (waypointId, entityId) => {
    setWaypoints(prev => prev.map(wp => {
      if (wp.id !== waypointId) return wp;
      return { ...wp, alternatives: wp.alternatives.filter(a => a.id !== entityId) };
    }));
  };

  const cancelOrSearch = (waypointId) => {
    updateWaypoint(waypointId, { showOrSearch: false, orQuery: '', orResults: [], orSearching: false });
  };

  // Helper: build waypoint ID (string or array for OR groups)
  const getWaypointIds = (wp) => {
    if (!wp.selected) return null;
    if (wp.alternatives.length === 0) return wp.selected.id;
    return [wp.selected.id, ...wp.alternatives.map(a => a.id)];
  };

const exploreEntity = async () => {
    // In exploration mode, only need first waypoint
    if (!waypoints[0].selected) {
      alert(t('search.alert_select_entity'));
      return;
    }

    setLoading(true);
    try {
      const response = await axios.get(`${config.API_BASE_URL}/api/explore/${waypoints[0].selected.id}`);

      // Format response to match path format for consistency
      onPathFound({
        found: true,
        exploration: true,
        entity: response.data.entity,
        subgraph: response.data.subgraph,
        neighbor_count: response.data.neighbor_count
      });

      setExplorationStarted(true);
    } catch (error) {
      console.error('Error exploring entity:', error);
      const errorMsg = error.response?.data?.error || error.message || 'Unknown error';
      alert(t('search.alert_explore_failed', { error: errorMsg }));
    } finally {
      setLoading(false);
    }
  };

  const searchTargetEntities = async (query) => {
    if (query.length < 2) {
      setTargetResults([]);
      setTargetSearching(false);
      return;
    }

    setTargetSearching(true);
    try {
      const response = await axios.get(`${config.API_BASE_URL}/api/search`, {
        params: { q: query }
      });
      setTargetResults(response.data.results || []);
    } catch (error) {
      console.error('Search error:', error);
      setTargetResults([]);
    } finally {
      setTargetSearching(false);
    }
  };

  const handleTargetChange = (value) => {
    setTargetQuery(value);
    setShowTargetResults(true);

    if (debounceTimers.current['target']) {
      clearTimeout(debounceTimers.current['target']);
    }
    if (value.length >= 2) {
      setTargetSearching(true);
      debounceTimers.current['target'] = setTimeout(() => {
        searchTargetEntities(value);
      }, 250);
    } else {
      setTargetResults([]);
      setTargetSearching(false);
    }
  };

  const selectTarget = (entity) => {
    setSelectedTarget(entity);
    setTargetQuery(`${entity.name} (${entity.id})`);
    setShowTargetResults(false);
  };

  const findPathInExploration = async () => {
    if (!selectedTarget) {
      alert(t('search.alert_select_target'));
      return;
    }

    // Get visible nodes from the graph (passed via callback)
    if (!graphRef || !graphRef.current) {
      alert(t('search.alert_graph_not_init'));
      return;
    }

    const visibleNodes = graphRef.current.getNodeIds();

    if (visibleNodes.length === 0) {
      alert(t('search.alert_no_visible'));
      return;
    }

    setPathLoading(true);
    try {
      // Find shortest path from any visible node to target
      let shortestPath = null;
      let shortestLength = Infinity;
      let bestSourceId = null;

      for (const sourceId of visibleNodes) {
        try {
          const response = await axios.post(`${config.API_BASE_URL}/api/top-paths`, {
            source: sourceId,
            target: selectedTarget.id,
            k: 1,
            exclude_insolvent: false,
            exclude_foreign: false,
            exclude_inactive: false
          });

          if (response.data.found && response.data.paths.length > 0) {
            const pathLength = response.data.paths[0].length;
            if (pathLength < shortestLength) {
              shortestLength = pathLength;
              shortestPath = response.data;
              bestSourceId = sourceId;
            }
          }
        } catch (error) {
          // Continue to next source
          continue;
        }
      }

      if (shortestPath) {
        // Merge this path with existing graph without destroying it
        onPathFound({
          found: true,
          exploration: true,
          preserveGraph: true,
          pathHighlight: shortestPath.paths[0],
          entity: { id: bestSourceId, name: 'Path Search' },
          subgraph: shortestPath.subgraph,
          neighbor_count: 0,
          pathFrom: bestSourceId,
          pathTo: selectedTarget.id
        });

        const sourceName = graphRef.current.getNodeLabel(bestSourceId);
        alert(t('search.alert_path_found_msg', { from: sourceName, to: selectedTarget.name, length: shortestLength }));
      } else {
        alert(t('search.alert_no_path', { name: selectedTarget.name }));
      }
    } catch (error) {
      console.error('Error finding path:', error);
      const errorMsg = error.response?.data?.error || error.message || 'Unknown error';
      alert(t('search.alert_path_error', { error: errorMsg }));
    } finally {
      setPathLoading(false);
    }
  };

const findPath = async () => {
    // Check that all waypoints are selected
    const allSelected = waypoints.every(wp => wp.selected);
    if (!allSelected) {
      alert(t('search.alert_select_all'));
      return;
    }

    setLoading(true);
    try {
      let response;

      if (useMultiPoint && waypoints.length > 2) {
        // Multi-point routing (with OR group support)
        response = await axios.post(`${config.API_BASE_URL}/api/multi-path`, {
          waypoints: waypoints.map(wp => getWaypointIds(wp)),
          exclude_insolvent: excludeInsolvent,
          exclude_foreign: excludeForeign,
          exclude_inactive: excludeInactive
        });

        // Convert single path response to match top-paths format
        if (response.data.found) {
          response.data.paths = [{
            path: response.data.path,
            length: response.data.path_length,
            details: response.data.details
          }];
          response.data.count = 1;
        }
      } else {
        // Standard 2-point routing with top K paths (with OR group support)
        response = await axios.post(`${config.API_BASE_URL}/api/top-paths`, {
          source: getWaypointIds(waypoints[0]),
          target: getWaypointIds(waypoints[1]),
          k: topK,
          exclude_insolvent: excludeInsolvent,
          exclude_foreign: excludeForeign,
          exclude_inactive: excludeInactive
        });

        // Filter paths by max length if set
        if (response.data.paths) {
          response.data.paths = response.data.paths.filter(
            path => path.length <= maxPathLength
          );
          response.data.count = response.data.paths.length;
        }
      }

      // Handle no path found
      if (!response.data.found || (response.data.paths && response.data.paths.length === 0)) {
        const message = response.data.message || 'No connection found';
        alert(t('search.alert_no_connection', { message }));
        return;
      }

      onPathFound(response.data);
    } catch (error) {
      console.error('Error finding path:', error);
      const errorMsg = error.response?.data?.error || error.message || 'Unknown error';
      alert(t('search.alert_find_failed', { error: errorMsg }));
    } finally {
      setLoading(false);
    }
  };

  const renderEntityResult = (entity) => {
    // Show entity with ID and type for disambiguation
    return (
      <div className="result-item-content">
        <div className="result-main">
          <strong>{entity.name}</strong>
          <span className="entity-id">ID: {entity.id}</span>
        </div>
        <span className={`entity-type ${entity.type}`}>
          {entity.type === 'company' ? `🏢 ${t('search.company')}` : `👤 ${t('search.person')}`}
        </span>
      </div>
    );
  };

  return (
    <div className="search-container">
      <h1>{t('search.title')}</h1>
      <p className="subtitle">{t('search.subtitle')}</p>

      {/* Mode Selection */}
      <div className="mode-toggles">
        <div className="multi-point-toggle">
          <label>
            <input
              type="checkbox"
              checked={explorationMode}
              onChange={(e) => {
                setExplorationMode(e.target.checked);
                if (e.target.checked) {
                  setUseMultiPoint(false);
                }
              }}
            />
            <span>{t('search.exploration_mode')}</span>
          </label>
        </div>

        {!explorationMode && (
          <div className="multi-point-toggle">
            <label>
              <input
                type="checkbox"
                checked={useMultiPoint}
                onChange={(e) => setUseMultiPoint(e.target.checked)}
              />
              <span>{t('search.multi_point')}</span>
            </label>
          </div>
        )}
      </div>

      {/* Waypoints */}
      <div className="waypoints-container">
        {waypoints.map((waypoint, index) => {
          // In exploration mode, only show first waypoint
          if (explorationMode && index > 0) return null;

          return (
          <div key={waypoint.id} className="search-box waypoint-box">
            <label>
              {explorationMode
                ? `${t('search.entity_to_explore')}:`
                : index === 0 ? `${t('search.start')}:` : index === waypoints.length - 1 ? `${t('search.end')}:` : `${t('search.waypoint', { index })}:`}
            </label>
            <div className="waypoint-input-group">
              <input
                type="text"
                value={waypoint.query}
                onChange={(e) => handleWaypointChange(waypoint.id, e.target.value)}
                onFocus={() => waypoint.query && updateWaypoint(waypoint.id, { showResults: true })}
                placeholder={t('search.placeholder')}
              />
              {waypoints.length > 2 && (
                <button
                  className="remove-waypoint-btn"
                  onClick={() => removeWaypoint(waypoint.id)}
                  title={t('search.remove_waypoint')}
                >
                  ✕
                </button>
              )}
            </div>
            {waypoint.showResults && (waypoint.searching || waypoint.results.length > 0) && (
              <div className="results-dropdown">
                {waypoint.searching && (
                  <div className="result-item searching">
                    <span className="loading-spinner small"></span>
                    <span>{t('search.searching')}</span>
                  </div>
                )}
                {waypoint.results.map((entity) => (
                  <div
                    key={entity.id}
                    className="result-item"
                    onClick={() => selectWaypoint(waypoint.id, entity)}
                  >
                    {renderEntityResult(entity)}
                  </div>
                ))}
              </div>
            )}

            {/* OR alternatives */}
            {!explorationMode && waypoint.selected && (
              <div className="or-group">
                {waypoint.alternatives.map((alt, altIdx) => (
                  <span key={alt.id} className="or-chip-wrapper">
                    <span className="or-label">OR</span>
                    <span className="or-chip">
                      <span className="or-chip-text">{alt.name} <span className="or-chip-id">({alt.id})</span></span>
                      <button className="or-chip-remove" onClick={() => removeOrAlternative(waypoint.id, alt.id)} title="Remove">&#xd7;</button>
                    </span>
                  </span>
                ))}

                {waypoint.showOrSearch ? (
                  <div className="or-search-inline">
                    <input
                      type="text"
                      value={waypoint.orQuery}
                      onChange={(e) => handleOrSearchChange(waypoint.id, e.target.value)}
                      placeholder={t('search.or_placeholder')}
                      className="or-search-input"
                      autoFocus
                    />
                    <button className="or-cancel-btn" onClick={() => cancelOrSearch(waypoint.id)}>&#xd7;</button>
                    {(waypoint.orSearching || waypoint.orResults.length > 0) && (
                      <div className="results-dropdown or-dropdown">
                        {waypoint.orSearching && (
                          <div className="result-item searching">
                            <span className="loading-spinner small"></span>
                            <span>{t('search.searching')}</span>
                          </div>
                        )}
                        {waypoint.orResults.map((entity) => (
                          <div key={entity.id} className="result-item" onClick={() => selectOrEntity(waypoint.id, entity)}>
                            {renderEntityResult(entity)}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                ) : (
                  waypoint.alternatives.length < 4 && (
                    <button className="or-add-btn" onClick={() => toggleOrSearch(waypoint.id)} title="Add OR alternative (same entity, different ID)">
                      {t('search.or_add')}
                    </button>
                  )
                )}
              </div>
            )}
          </div>
        );
        })}
      </div>

      {/* Add waypoint button */}
      {!explorationMode && useMultiPoint && waypoints.length < 5 && (
        <button className="add-waypoint-btn" onClick={addWaypoint}>
          {t('search.add_waypoint')}
        </button>
      )}

{/* Advanced Filters */}
      {!explorationMode && (
        <div className="search-filters">
          {!useMultiPoint && (
            <>
              <div className="filter-group">
                <label>{t('search.max_path_label')} <HelpTip text={t('search.max_path_tip')} /></label>
                <select value={maxPathLength} onChange={(e) => setMaxPathLength(Number(e.target.value))}>
                  <option value={3}>{t('search.steps_3')}</option>
                  <option value={4}>{t('search.steps_4')}</option>
                  <option value={5}>{t('search.steps_5')}</option>
                  <option value={10}>{t('search.steps_10')}</option>
                  <option value={999}>{t('search.no_limit')}</option>
                </select>
              </div>

              <div className="filter-group">
                <label>{t('search.num_paths_label')} <HelpTip text={t('search.num_paths_tip')} /></label>
                <select value={topK} onChange={(e) => setTopK(Number(e.target.value))}>
                  <option value={1}>{t('search.paths_1')}</option>
                  <option value={3}>{t('search.paths_3')}</option>
                  <option value={5}>{t('search.paths_5')}</option>
                </select>
              </div>
            </>
          )}

        <div className="filter-group checkbox-filter">
          <label>
            <input
              type="checkbox"
              checked={excludeInsolvent}
              onChange={(e) => setExcludeInsolvent(e.target.checked)}
            />
            <span>{t('search.exclude_insolvent')} <HelpTip text={t('search.exclude_insolvent_tip')} /></span>
          </label>
        </div>

        <div className="filter-group checkbox-filter">
          <label>
            <input
              type="checkbox"
              checked={excludeForeign}
              onChange={(e) => setExcludeForeign(e.target.checked)}
            />
            <span>{t('search.exclude_foreign')} <HelpTip text={t('search.exclude_foreign_tip')} /></span>
          </label>
        </div>

          <div className="filter-group checkbox-filter">
            <label>
              <input
                type="checkbox"
                checked={excludeInactive}
                onChange={(e) => setExcludeInactive(e.target.checked)}
              />
              <span>{t('search.active_only')} <HelpTip text={t('search.active_only_tip')} /></span>
            </label>
          </div>
        </div>
      )}

      <button
        onClick={explorationMode ? exploreEntity : findPath}
        disabled={explorationMode ? !waypoints[0].selected || loading : !waypoints.every(wp => wp.selected) || loading}
        className="find-path-btn"
      >
        {loading ? (
          <>
            <span className="loading-spinner"></span>
            {t('search.searching_btn')}
          </>
        ) : explorationMode ? t('search.explore_btn') : useMultiPoint && waypoints.length > 2 ? t('search.multi_point_btn') : t('search.find_path_btn')}
      </button>

      {/* Selected entities display */}
      {waypoints.some(wp => wp.selected) && (
        <div className="selected-entities">
          {waypoints.map((waypoint, index) => waypoint.selected && (
            <div key={waypoint.id} className="selected-entity">
              <span className="label">
                {index === 0 ? `${t('search.start')}:` : index === waypoints.length - 1 ? `${t('search.end')}:` : `${t('search.point', { index })}:`}
              </span>
              <span className="value">{waypoint.selected.name}</span>
              <span className="id">({waypoint.selected.id})</span>
              {waypoint.alternatives.length > 0 && waypoint.alternatives.map(alt => (
                <span key={alt.id} className="or-alt-display"> OR <span className="value">{alt.name}</span> <span className="id">({alt.id})</span></span>
              ))}
            </div>
          ))}
        </div>
      )}

      {/* Path finding within exploration mode */}
      {explorationMode && explorationStarted && (
        <div className="exploration-path-search">
          <h3>{t('search.find_path_to_node')}</h3>
          <p className="exploration-path-hint">
            {t('search.find_path_hint')}
          </p>

          <div className="search-box">
            <label>{t('search.target_entity')}:</label>
            <input
              type="text"
              value={targetQuery}
              onChange={(e) => handleTargetChange(e.target.value)}
              onFocus={() => targetQuery && setShowTargetResults(true)}
              placeholder={t('search.placeholder')}
            />
            {showTargetResults && (targetSearching || targetResults.length > 0) && (
              <div className="results-dropdown">
                {targetSearching && (
                  <div className="result-item searching">
                    <span className="loading-spinner small"></span>
                    <span>{t('search.searching')}</span>
                  </div>
                )}
                {targetResults.map((entity) => (
                  <div
                    key={entity.id}
                    className="result-item"
                    onClick={() => selectTarget(entity)}
                  >
                    {renderEntityResult(entity)}
                  </div>
                ))}
              </div>
            )}
          </div>

          <button
            onClick={findPathInExploration}
            disabled={!selectedTarget || pathLoading}
            className="find-path-btn exploration-path-btn"
          >
            {pathLoading ? (
              <>
                <span className="loading-spinner"></span>
                {t('search.searching_paths')}
              </>
            ) : t('search.find_from_visible')}
          </button>

          {selectedTarget && (
            <div className="selected-target-display">
              <strong>{t('search.target_label')}</strong> {selectedTarget.name} ({selectedTarget.id})
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default SearchBar;